import { useState, useCallback, useEffect } from "react";
import { ChevronLeft, Loader2, Plus, Trash2, X, Eye, Upload, Check, ChevronDown, ChevronUp, FileText, ReceiptText } from "lucide-react";
import toast from "react-hot-toast";
import { AdminOrderManagementService } from "../../../services/api/admin-order.service";
import { AdminOrderService } from "../../../services/api/AdminOrderService/admin.order.service";
import { Product, SelectedProduct, StockItem } from "../../Inventory-Stocks & Orders/Clinic/ClinicOrder/OfflineOrder/types";
import medicineIcon from "../../../assets/images/medicineIcon.png";
import defaultProduct from "../../../assets/images/default_product.jpg";
import type { Step1Data, OrderSummaryData } from "./types";

interface OrderDetailsStepProps {
  step1Data: Step1Data;
  onBack: () => void;
  onOrderCreated: () => void;
}

const OrderDetailsStep: React.FC<OrderDetailsStepProps> = ({ step1Data, onBack, onOrderCreated }) => {
  const { patient, selectedAddress, nearestClinic, orderFor } = step1Data;

  // ── Products ─────────────────────────────────────────────
  const [adminProducts, setAdminProducts] = useState<SelectedProduct[]>([]);

  // ── Add Medicine Modal ───────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1, itemsPerPage: 10, totalItemsCount: 0, totalPages: 1,
    nextPage: null as number | null, previousPage: null as number | null,
  });

  // ── Prescription Files ───────────────────────────────────
  const [prescriptionFiles, setPrescriptionFiles] = useState<File[]>([]);

  // ── Doctor Info ──────────────────────────────────────────
  const [showDoctorInfo, setShowDoctorInfo] = useState(false);
  const [doctorInfo, setDoctorInfo] = useState({ doctorName: "", doctorRegistrationNumber: "" });

  // ── Order Summary ────────────────────────────────────────
  const [orderSummary, setOrderSummary] = useState<OrderSummaryData | null>(null);
  const [fetchingSummary, setFetchingSummary] = useState(false);

  // ── Creating Order ───────────────────────────────────────
  const [creatingOrder, setCreatingOrder] = useState(false);

  // ── Fetch Clinic Stock ───────────────────────────────────
  const fetchProducts = useCallback(async (searchTerm = "", page = 1, itemsPerPage = 10) => {
    try {
      setModalLoading(true);
      const response = await AdminOrderService.getClinicProductListing(page, itemsPerPage, searchTerm, nearestClinic.clinicId);
      if (response?.data?.data && Array.isArray(response.data.data)) {
        const transformedProducts = response.data.data.map((item: StockItem) => ({
          compositeId: `admin_${item.id}_${item.product_id}`,
          id: item.id,
          product_id: item.product_id,
          product_name: item.product?.name || "Unknown Product",
          manufacturer_name: item.manufacturer?.manufacturer_name || "Unknown Manufacturer",
          product_image: item.product?.productImage || medicineIcon,
          landing_cost: parseFloat(item.landing_cost) || 0,
          mrp: parseFloat(item.productMrp || item.mrp),
          quantity: item.remaining_stocks || 0,
          expiry_date: item.expiry_date,
          batch_number: item.batch_number,
          hsn_number: item.hsn_number,
          product_rate: parseFloat(item.product_rate) || 0,
          discount: parseFloat(item.sellingDiscount) || 0,
          cgst: parseFloat(item.cgst) || 0,
          sgst: parseFloat(item.sgst) || 0,
          free_items: item.free_items || 0,
          finalSP: parseFloat(item.finalSP) || 0,
          sellingDiscount: parseFloat(item.sellingDiscount) || 0,
          remainingStocks: item.remaining_stocks,
          totalStock: item.remaining_stocks,
          noOfItemsInPack: item?.product?.tablet_per_stripe || 10,
          perPackPrice: parseFloat(item.productMrp || item.mrp),
          expiry: item.expiry_date,
          batch: item.batch_number,
          salt: item.salt,
        }));
        setAvailableProducts(transformedProducts);
        setPagination(response.data.pagination || { currentPage: 1, itemsPerPage: 10, totalItemsCount: 0, totalPages: 1, nextPage: null, previousPage: null });
      }
    } catch {
      toast.error("Failed to load clinic stock medicines");
    } finally {
      setModalLoading(false);
    }
  }, [nearestClinic.clinicId]);

  // Debounced search
  useEffect(() => {
    if (!showModal) return;
    const timer = setTimeout(() => { fetchProducts(searchText, 1, pagination.itemsPerPage); }, 500);
    return () => clearTimeout(timer);
  }, [searchText, showModal, fetchProducts, pagination.itemsPerPage]);

  const handleAddProduct = (product: Product) => {
    if (adminProducts.find((p) => p.compositeId === product.compositeId)) return;
    if (product.totalStock <= 0) { toast.error("Product is out of stock!"); return; }
    setAdminProducts((prev) => [...prev, { ...product, selectedQuantity: 0, substituteNote: "" }]);
    toast.success(`Added "${product.product_name}"`);
  };

  const handleRemoveProduct = (compositeId: string) => {
    setAdminProducts((prev) => prev.filter((p) => p.compositeId !== compositeId));
    setOrderSummary(null);
  };

  const onUpdateDiscount = (compositeId: string, discount: number) => {
    const product = adminProducts.find((p) => p.compositeId === compositeId);
    if (!product) return;
    if (isNaN(discount)) discount = 0;
    const currentPrice = product.perPackPrice * (1 - discount / 100);
    if (currentPrice < product.landing_cost) {
      toast.error(`Price (₹${currentPrice.toFixed(2)}) cannot be less than landing cost (₹${product.landing_cost.toFixed(2)})`);
      return;
    }
    setAdminProducts((prev) => prev.map((p) => (p.compositeId === compositeId ? { ...p, sellingDiscount: discount } : p)));
    setOrderSummary(null);
  };

  const getDaysRemaining = (expiryDate: string) => {
    if (!expiryDate) return null;
    const today = new Date(); const expiry = new Date(expiryDate);
    today.setHours(0, 0, 0, 0); expiry.setHours(0, 0, 0, 0);
    return Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };

  // ── Prescription File Handling ───────────────────────────
  const handleFileAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setPrescriptionFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
      e.target.value = "";
    }
  };
  const handleFileRemove = (idx: number) => {
    setPrescriptionFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  // ── Generate Order Summary ───────────────────────────────
  const handleGenerateSummary = async () => {
    if (adminProducts.length === 0) { toast.error("Please add at least one product"); return; }
    if (adminProducts.some((p) => !p.selectedQuantity || p.selectedQuantity === 0)) {
      toast.error("All products must have quantity > 0"); return;
    }
    setFetchingSummary(true);
    try {
      const productsPayload = adminProducts.map((p) => ({
        inwardId: p.id,
        itemsCount: p.selectedQuantity,
        price: parseFloat((p.perPackPrice * (1 - (p.sellingDiscount || 0) / 100)).toFixed(2)),
        mrp: p.mrp,
      }));
      const res = await AdminOrderManagementService.getOrderSummary({
        products: productsPayload,
        date: new Date().toISOString(),
        nearestClinicDistance: nearestClinic.distance,
      });
      if (res.success && res.data) {
        setOrderSummary(res.data);
        toast.success("Order summary generated!");
      } else {
        toast.error(res.message || "Failed to generate summary");
      }
    } catch {
      toast.error("Failed to generate order summary");
    } finally {
      setFetchingSummary(false);
    }
  };

  // ── Create Order ─────────────────────────────────────────
  const handleCreateOrder = async () => {
    if (!orderSummary) { toast.error("Please generate order summary first"); return; }
    setCreatingOrder(true);
    try {
      const formData = new FormData();
      formData.append("patientId", patient.patientId);
      formData.append("patientPhoneNumber", patient.patientMobileNumber);
      formData.append("orderTotal", orderSummary.totalAmount.toString());
      formData.append("hasPrescription", (prescriptionFiles.length > 0).toString());
      formData.append("prescriptionRequired", "true");
      formData.append("clinicId", nearestClinic.clinicId);
      formData.append("orderAddress", selectedAddress.address);
      formData.append("orderPincode", selectedAddress.pincode);
      formData.append("patientAddressId", selectedAddress.id.toString());

      if (doctorInfo.doctorName.trim()) formData.append("doctorName", doctorInfo.doctorName.trim());
      if (doctorInfo.doctorRegistrationNumber.trim()) formData.append("doctorRegistrationNumber", doctorInfo.doctorRegistrationNumber.trim());
      if (orderFor.type === "family" && orderFor.familyMemberId) formData.append("orderedFor", orderFor.familyMemberId);

      const products = adminProducts.map((p) => ({
        productId: p.product_id,
        noOfItems: p.selectedQuantity,
        price: parseFloat((p.perPackPrice * (1 - (p.sellingDiscount || 0) / 100)).toFixed(2)),
        mrp: p.mrp,
        inwardId: p.id || "",
      }));
      formData.append("products", JSON.stringify(products));

      const paymentSummary = {
        mrpTotal: orderSummary.totalMrp,
        discount: orderSummary.totalDiscount,
        medicalConsultationFee: orderSummary.medicalConsultationFee,
        totalGst: orderSummary.totalGst,
        coinsUsed: 0,
        taxAndCharges: orderSummary.taxesAndCharges,
        deliveryFee: orderSummary.deliveryFee,
        packagingCharges: orderSummary.packagingFee,
      };
      formData.append("orderPaymentSummary", JSON.stringify(paymentSummary));

      prescriptionFiles.forEach((file) => formData.append("image", file));

      const res = await AdminOrderManagementService.createAdminMedicineOrder(formData);
      if (res.success) {
        toast.success(`Order created successfully! ${res.data?.orderNumber || ""}`);
        onOrderCreated();
      } else {
        toast.error(res.message || "Failed to create order");
      }
    } catch {
      toast.error("An error occurred while creating the order");
    } finally {
      setCreatingOrder(false);
    }
  };

  return (
    <div className="flex gap-5">
      {/* ── Main Content ──────────────────────────────────────── */}
      <div className="flex-1 min-w-0">

        {/* Patient Details Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-slate-800">Patient Details</h3>
            <button
              onClick={onBack}
              className="flex items-center gap-1.5 px-3 py-1 text-xs font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors border-none cursor-pointer"
            >
              <ChevronLeft size={14} /> Edit Details
            </button>
          </div>

          {/* Top Row: Ordered By, Ordered For, Clinic */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pb-4 border-b border-slate-100">
            {/* Ordered By */}
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">
                Ordered By
              </span>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-bold text-xs border border-slate-200">
                  {patient.patientName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-800">{patient.patientName}</p>
                  <p className="text-[0.68rem] text-slate-400 font-medium">+91 {patient.patientMobileNumber}</p>
                </div>
              </div>
            </div>

            {/* Ordered For */}
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">
                Ordered For
              </span>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 font-bold text-xs border border-indigo-100">
                  {(orderFor.type === "self" ? patient.patientName : orderFor.familyMemberName || "F").charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-800">
                    {orderFor.type === "self" ? patient.patientName : orderFor.familyMemberName}
                  </p>
                  <p className="text-[0.68rem] text-slate-400 font-medium">+91 {patient.patientMobileNumber}</p>
                </div>
              </div>
            </div>

            {/* Clinic */}
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">
                Clinic
              </span>
              <div>
                <p className="text-xs font-bold text-slate-800">{nearestClinic.clinicName || nearestClinic.clinicId}</p>
                <p className="text-[0.68rem] text-slate-400 font-medium">{nearestClinic.distance.toFixed(2)} km away</p>
              </div>
            </div>
          </div>

          {/* Bottom Section: Delivery Address */}
          <div className="pt-3">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
              Delivery Address
            </span>
            <p className="text-xs font-medium text-slate-700 leading-relaxed">
              {selectedAddress.address}, {selectedAddress.city}, {selectedAddress.state} — {selectedAddress.pincode}
            </p>
          </div>
        </div>

        {/* Prescription Upload */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-2">
              <FileText size={14} className="text-emerald-500" /> Prescriptions
            </h3>
            <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 text-xs font-semibold text-emerald-600 hover:bg-emerald-100 transition-all cursor-pointer shadow-sm">
              <Upload size={13} /> Upload Prescription
              <input type="file" accept="image/*,application/pdf" className="hidden" multiple onChange={handleFileAdd} />
            </label>
          </div>
          {prescriptionFiles.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {prescriptionFiles.map((file, idx) => (
                <div key={idx} className="relative group px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg flex items-center gap-2">
                  <FileText size={14} className="text-slate-400" />
                  <span className="text-[0.68rem] font-semibold text-slate-600 max-w-[120px] truncate">{file.name}</span>
                  <button
                    onClick={() => handleFileRemove(idx)}
                    className="p-0.5 text-slate-400 hover:text-red-500 bg-transparent border-none cursor-pointer"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic">No prescriptions uploaded. Upload if available.</p>
          )}
        </div>

        {/* Doctor Information */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4 shadow-sm">
          <button
            className="w-full px-5 py-3.5 flex items-center justify-between font-bold text-xs text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
            onClick={() => setShowDoctorInfo(!showDoctorInfo)}
          >
            <span className="uppercase tracking-wider">Doctor Information (Optional)</span>
            {showDoctorInfo ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {showDoctorInfo && (
            <div className="px-5 pb-5 border-t border-slate-100 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[0.68rem] font-semibold text-slate-500 block mb-1">Doctor Name</label>
                  <input
                    type="text"
                    value={doctorInfo.doctorName}
                    onChange={(e) => setDoctorInfo((d) => ({ ...d, doctorName: e.target.value }))}
                    placeholder="Enter doctor's name"
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all bg-slate-50/50"
                  />
                </div>
                <div>
                  <label className="text-[0.68rem] font-semibold text-slate-500 block mb-1">Doctor Registration Number</label>
                  <input
                    type="text"
                    value={doctorInfo.doctorRegistrationNumber}
                    onChange={(e) => setDoctorInfo((d) => ({ ...d, doctorRegistrationNumber: e.target.value }))}
                    placeholder="Enter registration number"
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all bg-slate-50/50"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4 shadow-sm flex flex-col min-h-[400px]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Selected Medicines</h3>
            <button
              onClick={() => setShowModal(true)}
              className="px-3.5 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100 transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <Plus size={14} /> Add Medicine
            </button>
          </div>

          {/* Table Header */}
          <div className="grid grid-cols-[3fr_1.2fr_1.2fr_1.2fr_1.2fr_1.2fr_0.6fr] gap-2 text-[10px] font-bold text-slate-400 uppercase border-b border-slate-100 pb-2 mb-2 w-full pr-2">
            <div>Name</div><div>MRP</div><div>Price</div><div className="text-center">Quantity</div><div>Discount (%)</div><div>Total</div><div className="text-center">Action</div>
          </div>

          {adminProducts.length === 0 ? (
            <div className="flex-1 flex items-center justify-center py-12 text-slate-400 text-xs">
              No medicines added yet. Click <b>"Add Medicine"</b> to add clinic stocks.
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto w-full pr-2 max-h-[400px]">
              {adminProducts.map((product) => {
                const finalPrice = product.perPackPrice * (1 - (product.sellingDiscount || 0) / 100) * product.selectedQuantity;
                const unitPrice = product.perPackPrice * (1 - (product.sellingDiscount || 0) / 100);
                return (
                  <div
                    key={product.compositeId}
                    className="grid grid-cols-[3fr_1.2fr_1.2fr_1.2fr_1.2fr_1.2fr_0.6fr] gap-2 items-center py-3 border-b border-emerald-50/50 bg-emerald-50/5 hover:bg-emerald-50/20 transition-colors w-full rounded-lg px-1 mb-1.5"
                  >
                    <div className="flex flex-col min-w-0 pr-2">
                      <div className="text-[12px] font-bold text-slate-700 truncate" title={product.product_name}>{product.product_name}</div>
                      <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-400 truncate">
                        <span className="truncate">{product.manufacturer_name}</span>
                        {product.batch_number && <span className="font-medium bg-slate-100 px-1 rounded flex-shrink-0">Batch: {product.batch_number}</span>}
                      </div>
                    </div>
                    <div className="text-[11px] text-slate-600 font-bold whitespace-nowrap">₹ {product.mrp?.toFixed(2)}</div>
                    <div className="text-[11px] text-slate-600 font-semibold whitespace-nowrap">₹ {unitPrice.toFixed(2)}</div>
                    <div className="flex justify-center px-1">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={product.selectedQuantity || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "" || /^\d*$/.test(val)) {
                            const qty = val === "" ? 0 : parseInt(val, 10);
                            if (qty > product.totalStock) { toast.error(`Only ${product.totalStock} items left`); return; }
                            setAdminProducts((prev) => prev.map((p) => p.compositeId === product.compositeId ? { ...p, selectedQuantity: qty } : p));
                            setOrderSummary(null);
                          }
                        }}
                        className="w-12 px-1 py-0.5 text-center border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-white shadow-sm font-semibold"
                      />
                    </div>
                    <div className="px-1">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={product.sellingDiscount === 0 ? "0" : product.sellingDiscount || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "" || /^(\d+\.?\d*|\.\d+)$/.test(val)) onUpdateDiscount(product.compositeId, parseFloat(val) || 0);
                        }}
                        className="w-full px-2 py-0.5 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-white font-semibold text-slate-700"
                      />
                    </div>
                    <div className="text-[12px] font-extrabold text-emerald-600">₹ {finalPrice.toFixed(2)}</div>
                    <div className="flex justify-center">
                      <button onClick={() => handleRemoveProduct(product.compositeId)} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer border-none bg-transparent">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Right Sidebar ─────────────────────────────────────── */}
      <div className="w-[300px] flex-shrink-0 hidden lg:block">
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm sticky top-5 flex flex-col gap-4">

          {/* Products Summary */}
          <div>
            <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Products ({adminProducts.length})</h4>
            <div className="flex flex-col gap-2 max-h-[150px] overflow-y-auto pr-1" style={{ scrollbarWidth: "thin" }}>
              {adminProducts.map((p) => {
                const finalSub = p.perPackPrice * (1 - (p.sellingDiscount || 0) / 100) * p.selectedQuantity;
                return (
                  <div key={p.compositeId} className="flex justify-between items-center text-xs">
                    <div className="min-w-0 pr-2">
                      <p className="font-bold text-slate-600 truncate">{p.product_name}</p>
                      <span className="text-[9px] font-bold bg-emerald-50 text-emerald-500 border border-emerald-100 rounded px-1">× {p.selectedQuantity}</span>
                    </div>
                    <span className="font-bold text-emerald-600 whitespace-nowrap">₹{finalSub.toFixed(2)}</span>
                  </div>
                );
              })}
              {adminProducts.length === 0 && <p className="text-xs text-slate-400 italic">No products added yet.</p>}
            </div>
          </div>

          <hr className="border-slate-100 my-0.5" />

          {/* Generate Summary Button */}
          <button
            onClick={handleGenerateSummary}
            disabled={fetchingSummary || adminProducts.length === 0 || adminProducts.some((p) => !p.selectedQuantity)}
            className="w-full py-2.5 rounded-lg text-xs font-bold bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:shadow-lg transition-all border-none cursor-pointer disabled:opacity-40 flex items-center justify-center gap-1.5"
          >
            {fetchingSummary ? <><Loader2 size={14} className="animate-spin" /> Generating...</> : <><ReceiptText size={14} /> Generate Order Summary</>}
          </button>

          {/* Order Summary Details */}
          {orderSummary && (
            <div className="flex flex-col gap-2.5 text-xs font-semibold text-slate-500">
              <div className="flex justify-between"><span>Total MRP</span><span className="text-slate-700 font-bold">₹{orderSummary.totalMrp.toFixed(2)}</span></div>
              <div className="flex justify-between text-emerald-600 bg-emerald-50/50 px-2 py-1.5 rounded-lg border border-emerald-100/50">
                <span className="font-bold">Discount</span><span className="font-extrabold">- ₹{orderSummary.totalDiscount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-[11px] text-slate-400 pl-2"><span>GST</span><span>₹{orderSummary.totalGst.toFixed(2)}</span></div>
              <div className="flex justify-between text-[11px] text-slate-400 pl-2"><span>Delivery Fee</span><span>₹{orderSummary.deliveryFee.toFixed(2)}</span></div>
              {orderSummary.packagingFee > 0 && (
                <div className="flex justify-between text-[11px] text-slate-400 pl-2"><span>Packaging</span><span>₹{orderSummary.packagingFee.toFixed(2)}</span></div>
              )}
              <hr className="border-slate-100 my-0.5" />
              <div className="flex justify-between items-center text-sm font-extrabold text-slate-800 bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                <span>Total Payable</span>
                <span className="text-lg font-black text-emerald-600">₹{orderSummary.totalAmount}</span>
              </div>
              {orderSummary.deliveryTimeText && (
                <div className="text-[0.65rem] text-slate-400 bg-slate-50 rounded-lg p-2 text-center leading-relaxed">
                  🕐 {orderSummary.deliveryTimeText}
                </div>
              )}
            </div>
          )}

          {/* Create Order Button */}
          <button
            onClick={handleCreateOrder}
            disabled={creatingOrder || !orderSummary || adminProducts.length === 0}
            className="w-full py-2.5 rounded-lg text-xs font-bold bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg transition-all border-none cursor-pointer disabled:opacity-40 flex items-center justify-center gap-1.5"
          >
            {creatingOrder ? <><Loader2 size={14} className="animate-spin" /> Creating...</> : <><Check size={14} /> Create Order</>}
          </button>
        </div>
      </div>

      {/* ── Add Medicines Modal ────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] md:max-w-6xl max-h-[90vh] flex flex-col overflow-hidden animate-in">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
              <h2 className="text-lg font-bold text-slate-800">Add Medicines</h2>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by name, batch, manufacturer..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-xs w-[280px] focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  />
                  <Plus size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 rotate-45" />
                </div>
                <button onClick={() => setShowModal(false)} className="p-1.5 hover:bg-slate-100 rounded-full transition-colors border-none bg-transparent cursor-pointer">
                  <X size={20} className="text-slate-500" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 flex flex-col overflow-hidden p-6 bg-white">
              {/* Headers */}
              <div className="grid grid-cols-[2fr_1fr_1fr_0.8fr_1fr_1fr_0.6fr_0.6fr_0.8fr] gap-2 px-4 py-2 bg-slate-50 rounded-lg text-[10px] font-bold text-slate-400 uppercase mb-2">
                <div>Name</div><div>Batch</div><div>Expiry</div><div>Qty/Pack</div><div>MRP</div><div>MRP/Unit</div><div className="text-center">Strips</div><div className="text-center">Tablets</div><div className="text-center">Action</div>
              </div>

              {/* Stock List */}
              <div className="flex-1 overflow-y-auto min-h-[350px] pr-1" style={{ scrollbarWidth: "thin" }}>
                {modalLoading ? (
                  <div className="flex items-center justify-center py-16 text-slate-400">
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 size={24} className="animate-spin text-emerald-500" />
                      <span className="text-xs">Loading stock medicines...</span>
                    </div>
                  </div>
                ) : availableProducts.length === 0 ? (
                  <div className="flex items-center justify-center py-16 text-slate-400 text-xs">No stock products found.</div>
                ) : (
                  availableProducts.map((product) => {
                    const daysLeft = getDaysRemaining(product.expiry_date);
                    const isAdded = adminProducts.some((p) => p.compositeId === product.compositeId);
                    const mrpUnit = product.mrp / (product.noOfItemsInPack || 1);
                    return (
                      <div
                        key={product.compositeId}
                        className="grid grid-cols-[2fr_1fr_1fr_0.8fr_1fr_1fr_0.6fr_0.6fr_0.8fr] gap-2 items-center px-4 py-3 border-b border-slate-50 hover:bg-slate-50/50 transition-colors text-xs font-semibold text-slate-700"
                      >
                        <div className="flex items-center gap-3 pr-2 min-w-0">
                          <img src={product.product_image || defaultProduct} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0 border border-slate-100" />
                          <div className="min-w-0">
                            <div className="text-[12px] font-bold text-slate-800 truncate" title={product.product_name}>{product.product_name}</div>
                            <div className="text-[10px] text-slate-400 truncate" title={product.manufacturer_name}>{product.manufacturer_name}</div>
                          </div>
                        </div>
                        <div className="truncate text-slate-600">{product.batch_number || "-"}</div>
                        <div>
                          <span className={`text-[11px] font-bold ${daysLeft !== null && daysLeft < 30 ? "text-red-500" : "text-slate-500"}`}>
                            {daysLeft !== null ? (daysLeft > 0 ? `${daysLeft} days` : "Expired") : "-"}
                          </span>
                        </div>
                        <div className="text-slate-500 font-semibold">{product.noOfItemsInPack}</div>
                        <div className="text-slate-600 font-bold">₹ {product.mrp?.toFixed(2)}</div>
                        <div className="text-slate-500 font-semibold">₹ {mrpUnit.toFixed(1)}</div>
                        <div className="text-center text-slate-500">{product.remainingStocks}</div>
                        <div className="text-center text-slate-400">0</div>
                        <div className="flex justify-center">
                          <button
                            onClick={() => (isAdded ? handleRemoveProduct(product.compositeId) : handleAddProduct(product))}
                            className="px-4 py-1.5 rounded-lg text-white text-[11px] font-bold border-none transition-all hover:shadow cursor-pointer"
                            style={{ backgroundColor: isAdded ? "#DC2626" : "#10B981" }}
                          >
                            {isAdded ? "Remove" : "Add"}
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Modal Pagination */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50 rounded-lg mt-3">
                <span className="text-[11px] font-bold text-slate-400">Page {pagination.currentPage} of {pagination.totalPages || 1}</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => { if (pagination.currentPage > 1) fetchProducts(searchText, pagination.currentPage - 1, pagination.itemsPerPage); }}
                    disabled={pagination.currentPage === 1}
                    className="px-3 py-1.5 text-[11px] font-bold rounded border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >Previous</button>
                  <button
                    onClick={() => { if (pagination.currentPage < (pagination.totalPages || 1)) fetchProducts(searchText, pagination.currentPage + 1, pagination.itemsPerPage); }}
                    disabled={pagination.currentPage === (pagination.totalPages || 1)}
                    className="px-3 py-1.5 text-[11px] font-bold rounded border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >Next</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetailsStep;
